import { isPlatformServer } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { createStore } from '@ngneat/elf';
import {
  selectEntity,
  selectEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { joinRequestResult, trackRequestResult } from '@ngneat/elf-requests';
import { Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

import { CmsPageResponse } from '@digital-platform/rs-cms/domain';
import { CmsContentService } from '../cms-content/cms-content.service';

export interface CmsPage {
  path: string;
  content: CmsPageResponse;
}

@Injectable({
  providedIn: 'root',
})
export class CmsPagesService {
  public pages$: Observable<Record<string, CmsPage>>;

  private cmsContent = inject(CmsContentService);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    this.pages$ = this.store.pipe(selectEntities());
  }

  private store = createStore(
    { name: 'cmsPages' },
    withEntities<CmsPage, 'path'>({ idKey: 'path' })
  );

  public fetchPage(path: string): Observable<void> {
    return this.cmsContent.getPageAsJson(path).pipe(
      trackRequestResult(['cmsPage', path], {
        cacheResponseData: true,
        // Skip ElfRequest cache on SSR because it messes with TransferState
        skipCache: isPlatformServer(this.platformId),
      }),
      tap((page) => this.setPage(path, page)),
      map(() => void 0)
    );
  }

  public selectPage(path: string): Observable<CmsPage | undefined> {
    return this.store.pipe(
      selectEntity(path),
      joinRequestResult<CmsPage | undefined, unknown, CmsPage>([
        'cmsPage',
        path,
      ]),
      filter((result): boolean => !result.isLoading),
      map((result) => result?.data)
    );
  }

  private setPage(path: string, content: CmsPageResponse): void {
    this.store.update(upsertEntities({ path, content }));
  }
}
